import { useEffect } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useAccountCoupons } from '@/data/Content/AccountCoupons';
import {
	PERSONALIZATION_FALLBACK_PATH,
	PERSONALIZATION_LOGIN_ORIGIN,
	useRepurchaseCoupon,
} from '@/data/Content/RepurchaseCoupon';
import { useNextRouter } from '@/data/Content/_NextRouter';
import { useUser } from '@/data/User';
import {
	CouponUrgency,
	formatCouponDate,
	getCouponUrgency,
} from '@/components/blocks/DpRepurchaseCoupon/formatCouponDate';
import { fillPersonalization } from '@/components/blocks/DpRepurchaseCoupon/fillPersonalization';
import { HighlightedText } from '@/components/blocks/HighlightedText';
import {
	COUNTDOWN_SECONDS_TICK_MS,
	CouponCountdown,
	useCouponCountdown,
} from '@/components/blocks/DpRepurchaseCoupon/useCouponCountdown';
import { usePersonalizationTokens } from '@/components/blocks/DpRepurchaseCoupon/usePersonalizationTokens';
import { ArrowRightIcon } from '@/components/blocks/DpRepurchaseCoupon/parts/CouponIcons';
import {
	RepurchaseLoginFlow,
	useRepurchaseActivation,
} from '@/components/blocks/DpRepurchaseCoupon/parts/RepurchaseActivateCheck';
import {
	heroButtonSX,
	heroCardDividerSX,
	heroCardLabelSX,
	heroCardOffSX,
	heroCardPercentageSX,
	heroCardStatusSX,
	heroCardStrongSX,
	heroCardSX,
	heroCardTermItemSX,
	heroCardTermsSX,
	heroContentSX,
	heroCountdownLabelSX,
	heroCountdownSX,
	heroCountdownUnitLabelSX,
	heroCountdownUnitSX,
	heroCountdownValueSX,
	heroEyebrowSX,
	heroInnerSX,
	heroRootSX,
	heroTextStrongSX,
	heroTextSX,
	heroTitleSX,
} from '@/components/blocks/DpRepurchaseCoupon/styles/hero';

export type RepurchaseHeroContent = {
	eyebrow?: string;
	title?: string;
	text?: string;
	pendingTitle?: string;
	pendingText?: string;
	guestTitle?: string;
	guestText?: string;
	titleToday?: string;
	titleTomorrow?: string;
	countdownLabel?: string;
	activateButtonLabel?: string;
	guestButtonLabel?: string;
	cardLabel?: string;
	cardNote?: string;
	terms?: string;
};

type HeroState = 'guest' | 'pending' | 'applied';

const TITLE_ID = 'dp-repurchase-hero-title';

const CARD_STATUS: Record<Exclude<HeroState, 'applied'>, string> = {
	guest: 'Se activa al iniciar sesión',
	pending: 'Listo para activar en tu cuenta',
};
const COUNTDOWN_PLACEHOLDER = '--';
const TERMS_SEPARATOR_PATTERN = /\s*[·|]\s*/;

const toTermItems = (note: string, terms: string) =>
	[note, ...terms.split(TERMS_SEPARATOR_PATTERN)].map((item) => item.trim()).filter(Boolean);

const COUNTDOWN_UNITS: { key: keyof CouponCountdown; label: string }[] = [
	{ key: 'days', label: 'Días' },
	{ key: 'hours', label: 'Horas' },
	{ key: 'minutes', label: 'Min' },
	{ key: 'seconds', label: 'Seg' },
];

const formatUnit = (value?: number) =>
	value === undefined ? COUNTDOWN_PLACEHOLDER : String(value).padStart(2, '0');

const CountdownBoxes = ({ label, dateTime }: { label: string; dateTime?: string }) => {
	const { countdown } = useCouponCountdown(dateTime, COUNTDOWN_SECONDS_TICK_MS);

	return (
		<>
			<Typography component="p" sx={heroCountdownLabelSX}>
				{label}
			</Typography>
			<Box component="ul" role="timer" aria-live="off" sx={heroCountdownSX}>
				{COUNTDOWN_UNITS.map(({ key, label: unitLabel }) => (
					<Box component="li" key={key} sx={heroCountdownUnitSX}>
						<Typography component="span" sx={heroCountdownValueSX}>
							{formatUnit(countdown?.[key])}
						</Typography>
						<Typography component="span" sx={heroCountdownUnitLabelSX}>
							{unitLabel}
						</Typography>
					</Box>
				))}
			</Box>
		</>
	);
};

const CouponSummaryCard = ({
	label,
	percentage,
	firstName,
	state,
	expirationDate,
	note,
	terms,
}: {
	label: string;
	percentage: number;
	firstName?: string;
	state: HeroState;
	expirationDate?: string;
	note: string;
	terms: string;
}) => (
	<Box component="aside" aria-label={label} sx={heroCardSX}>
		<Typography component="p" sx={heroCardLabelSX}>
			{label}
		</Typography>
		<Typography component="p" sx={heroCardPercentageSX}>
			{`${percentage}%`}
			<Box component="span" sx={heroCardOffSX}>
				{'OFF'}
			</Box>
		</Typography>
		<Typography component="p" sx={heroCardStatusSX}>
			{state === 'applied' ? (
				firstName ? (
					<>
						{'Activo en la cuenta de '}
						<Box component="strong" sx={heroCardStrongSX}>
							{firstName}
						</Box>
					</>
				) : (
					'Activo en tu cuenta'
				)
			) : (
				CARD_STATUS[state]
			)}
		</Typography>
		<Box sx={heroCardDividerSX} />
		{expirationDate ? (
			<Typography component="p" sx={heroCardStatusSX}>
				{'Vence el '}
				<Box component="strong" sx={heroCardStrongSX}>
					{expirationDate}
				</Box>
			</Typography>
		) : null}
		<Box component="ul" sx={heroCardTermsSX}>
			{toTermItems(note, terms).map((item) => (
				<Box component="li" key={item} sx={heroCardTermItemSX}>
					<HighlightedText text={item} sx={heroCardStrongSX} />
				</Box>
			))}
		</Box>
	</Box>
);

const HERO_DEFAULTS: Required<RepurchaseHeroContent> = {
	eyebrow: '{nombre}, esto es solo para ti',
	title: 'Tu {porcentaje} OFF ya está activo',
	text: 'Elegimos todo lo que ves aquí a partir de tu última compra y en tu talla. Sin códigos: tu descuento se aplica solo al pagar.',
	pendingTitle: 'Tu {porcentaje} OFF está listo para ti',
	pendingText: 'Actívalo y tu descuento se aplica solo al pagar, sin códigos.',
	guestTitle: 'Tu {porcentaje} OFF te está esperando',
	guestText: 'Inicia sesión para activar tu descuento. Se aplica solo al pagar, sin códigos.',
	titleToday: '¡Tu {porcentaje} OFF vence hoy!',
	titleTomorrow: '¡Tu {porcentaje} OFF vence mañana!',
	countdownLabel: 'Tu descuento se va en',
	activateButtonLabel: 'Activar cupón',
	guestButtonLabel: 'Inicia sesión para activarlo',
	cardLabel: 'Tu descuento exclusivo',
	cardNote: 'En la pieza de mayor precio de tu compra',
	terms: 'Un solo uso · No aplica con otras promociones',
};

const withHeroDefaults = (content: RepurchaseHeroContent): Required<RepurchaseHeroContent> => ({
	...HERO_DEFAULTS,
	...Object.fromEntries(
		Object.entries(content).filter(([, value]) => typeof value === 'string' && !!value.trim())
	),
});

const formatExpirationDate = (dateTime?: string) =>
	dateTime ? formatCouponDate(dateTime) : undefined;

const HERO_COPY: Record<
	HeroState,
	{ title: keyof RepurchaseHeroContent; text: keyof RepurchaseHeroContent }
> = {
	guest: { title: 'guestTitle', text: 'guestText' },
	pending: { title: 'pendingTitle', text: 'pendingText' },
	applied: { title: 'title', text: 'text' },
};

const URGENCY_TITLE: Record<CouponUrgency, keyof RepurchaseHeroContent> = {
	today: 'titleToday',
	tomorrow: 'titleTomorrow',
};

const getHeroCopy = (
	content: Required<RepurchaseHeroContent>,
	state: HeroState,
	urgency?: CouponUrgency
) => ({
	title: content[urgency ? URGENCY_TITLE[urgency] : HERO_COPY[state].title],
	text: content[HERO_COPY[state].text],
});

const HeroActivateButton = ({ label, onApply }: { label: string; onApply: () => void }) => {
	const { loginOpen, onActivate, onLoginClose, onLoginSuccess } = useRepurchaseActivation(
		onApply,
		'landing'
	);

	return (
		<>
			<ButtonBase onClick={onActivate} sx={heroButtonSX}>
				{label}
				<ArrowRightIcon />
			</ButtonBase>
			<RepurchaseLoginFlow
				open={loginOpen}
				origin={PERSONALIZATION_LOGIN_ORIGIN}
				onClose={onLoginClose}
				onSuccess={onLoginSuccess}
			/>
		</>
	);
};

const useLeaveLandingWhen = (leave: boolean) => {
	const router = useNextRouter();
	useEffect(() => {
		if (leave) {
			router.replace(PERSONALIZATION_FALLBACK_PATH);
		}
	}, [leave, router]);
};

const getHeroState = (isLoggedIn: boolean, applied: boolean): HeroState => {
	if (!isLoggedIn) {
		return 'guest';
	}
	return applied ? 'applied' : 'pending';
};

const useHeroCoupon = () => {
	const { coupon, redeemable, applyCoupon } = useRepurchaseCoupon();
	const { coupons } = useAccountCoupons();
	const { user } = useUser();
	const { ready, countdown } = useCouponCountdown(coupon?.expirationDateTime);
	const isLoggedIn = !!user?.isLoggedIn;
	const unavailable = isLoggedIn && !!coupons && !redeemable;
	const expired = ready && !countdown && !!coupon?.expirationDateTime;

	useLeaveLandingWhen(unavailable || expired);

	return {
		coupon: unavailable || expired ? undefined : coupon,
		state: getHeroState(isLoggedIn, coupon?.status === 'APPLIED'),
		applyCoupon,
		showCountdown: !!coupon?.expirationDateTime && (!ready || !!countdown),
	};
};

export const RepurchaseHero = (props: RepurchaseHeroContent) => {
	const content = withHeroDefaults(props);
	const tokens = usePersonalizationTokens();
	const { coupon, state, applyCoupon, showCountdown } = useHeroCoupon();

	if (!coupon) {
		return null;
	}

	const fill = (template: string) => fillPersonalization(template, tokens);
	const expirationDate = formatExpirationDate(coupon.expirationDateTime);
	const copy = getHeroCopy(content, state, getCouponUrgency(coupon.expirationDateTime));

	return (
		<Box component="section" aria-labelledby={TITLE_ID} sx={heroRootSX}>
			<Box sx={heroInnerSX}>
				<Box sx={heroContentSX}>
					<Typography component="p" sx={heroEyebrowSX}>
						{fill(content.eyebrow)}
					</Typography>
					<Typography id={TITLE_ID} component="h1" sx={heroTitleSX}>
						{fill(copy.title)}
					</Typography>
					<Typography component="p" sx={heroTextSX}>
						<HighlightedText text={fill(copy.text)} sx={heroTextStrongSX} />
					</Typography>
					{showCountdown ? (
						<CountdownBoxes
							label={fill(content.countdownLabel)}
							dateTime={coupon.expirationDateTime}
						/>
					) : null}
					{state !== 'applied' ? (
						<HeroActivateButton
							label={fill(
								state === 'guest' ? content.guestButtonLabel : content.activateButtonLabel
							)}
							onApply={applyCoupon}
						/>
					) : null}
				</Box>
				<CouponSummaryCard
					label={fill(content.cardLabel)}
					percentage={coupon.percentage}
					firstName={tokens.nombre}
					state={state}
					expirationDate={expirationDate}
					note={fill(content.cardNote)}
					terms={fill(content.terms)}
				/>
			</Box>
		</Box>
	);
};
